import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Badge as BadgeIcon, Save as SaveIcon } from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showSuccess, showError } from '../../utils/sweetAlertConfig';

const CandidateSSNCompletion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ssn, setSsn] = useState('');
  const [error, setError] = useState('');
  const [extractedData, setExtractedData] = useState(null);

  const extractDataFromSSN = (ssnValue) => {
    if (!ssnValue || ssnValue.length !== 14) {
      setError('National ID must be 14 digits');
      setExtractedData(null);
      return null;
    }

    if (!/^\d{14}$/.test(ssnValue)) {
      setError('National ID must contain only numbers');
      setExtractedData(null);
      return null;
    }

    try {
      const century = ssnValue[0];
      const yearDigits = ssnValue.substring(1, 3);
      const month = ssnValue.substring(3, 5);
      const day = ssnValue.substring(5, 7);
      const genderDigit = parseInt(ssnValue[12]);

      const monthNum = parseInt(month);
      const dayNum = parseInt(day);

      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        setError('Invalid date in National ID');
        setExtractedData(null);
        return null;
      }

      const year = century === '2' ? `19${yearDigits}` : `20${yearDigits}`;
      const birthDate = `${year}-${month}-${day}`;
      const gender = genderDigit % 2 === 0 ? 'Female' : 'Male';

      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      const data = { birthDate, gender, age };
      setExtractedData(data);
      setError('');
      return data;
    } catch (err) {
      setError('Invalid National ID format');
      setExtractedData(null);
      return null;
    }
  };

  const handleSSNChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 14);
    setSsn(value);
    if (value.length === 14) {
      extractDataFromSSN(value);
    } else {
      setExtractedData(null);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (ssn.length !== 14) {
      setError('Please enter a valid 14-digit National ID');
      return;
    }

    const data = extractDataFromSSN(ssn);
    if (!data) return;

    setLoading(true);
    try {
      await api.put('/candidate/profile', {
        ssn: ssn,
        birth_date: data.birthDate,
        gender: data.gender,
        age: data.age,
      });

      showSuccess('Profile completed successfully!');
      setTimeout(() => {
        navigate('/candidate/assessments');
      }, 500);
    } catch (err) {
      console.error('Error saving SSN:', err);
      showError('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <BadgeIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" fontWeight={600} color="primary.main" gutterBottom>
                Complete Your Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please enter your National ID to continue
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                required
                label="National ID (14 digits)"
                value={ssn}
                onChange={handleSSNChange}
                error={!!error}
                helperText={error}
                inputProps={{ maxLength: 14 }}
                sx={{ mb: 3 }}
                placeholder="29501011234567"
              />

              {extractedData && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    disabled
                    label="Birth Date"
                    value={extractedData.birthDate}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    disabled
                    label="Age"
                    value={`${extractedData.age} years`}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    disabled
                    label="Gender"
                    value={extractedData.gender}
                  />
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !extractedData}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
              >
                {loading ? 'Saving...' : 'Continue'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CandidateSSNCompletion;
